// RITA-1585/RITA-1602 — Gemeinsamer Baustein von Leiste und Kachel: Kapitelauswahl und Suche oben, darunter die
// Kapitelüberschrift und der Text des Kapitels; bei einer Sucheingabe stehen dort die Treffer. Die Kachel zeigt
// zusätzlich die Handbuch-Auswahl und sucht über alle Handbücher; die Leiste bleibt beim Handbuch der Seite.
//
// Bildlauf: In der Leiste (`eigenerBildlauf`) bleiben Auswahl und Suche stehen, nur der Text darunter rollt. Auf der
// Kachel rollt die ganze Seite. In der Leiste beginnt ein neues Kapitel oben, auf der Kachel bleibt die Seite bei der Wahl
// stehen; ein Verweis oder Suchtreffer springt an die Stelle.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import useHandbuchTexte, { HandbuchLader } from '../hooks/useHandbuchTexte';
import {
  Handbuch,
  HANDBUECHER,
  handbuchFinden,
  kapitelBezeichnung,
  kapitelFinden,
  Kapitelwahl,
} from '../model/handbuecher';
import { HandbuchZiel } from '../model/handbuchInhalt';
import { handbuchSuchen, suchwoerter, textMarkieren } from '../model/handbuchSuche';
import HandbuchText from './HandbuchText';

export const HANDBUCH_AUSWAHL_FELDNAME = 'Handbuch';
export const KAPITEL_AUSWAHL_FELDNAME = 'Kapitel';
export const HANDBUCH_SUCHE_LEISTE = 'Im Handbuch suchen …';
export const HANDBUCH_SUCHE_ALLE = 'In allen Handbüchern suchen …';
export const HANDBUCH_KEINE_TREFFER = 'Keine Treffer.';
export const HANDBUCH_TEXT_LAEDT = 'Der Text wird geladen …';
export const HANDBUCH_TEXT_FEHLER = 'Der Text konnte nicht geladen werden.';
export const HANDBUCH_SUCHE_FEHLER = 'Der Text konnte nicht durchsucht werden. Gefunden wurden nur Kapitelnamen.';
export const HANDBUCH_ERNEUT = 'Erneut versuchen';

/** Wohin nach dem Wechsel gesprungen wird, und welche Wörter markiert bleiben. */
interface Sprung {
  abschnittId?: string;
  block?: number;
  /** Verweis auf ein ganzes Kapitel: Auch wenn es schon offen ist, geht es an seinen Anfang. */
  oben?: boolean;
  woerter?: readonly string[];
}

interface Props {
  auswahl: Kapitelwahl;
  onAuswahl: (wahl: Kapitelwahl, abschnittId?: string) => void;
  /** Kachel: Handbuch-Auswahl über der Kapitelauswahl, Suche über alle Handbücher. Leiste: nur das gewählte Handbuch. */
  alleHandbuecher?: boolean;
  handbuecher?: readonly Handbuch[];
  /** Überschrift des Kapitels: in der Leiste unter der Leistenüberschrift (h3), auf der Kachel unter dem Seitenkopf (h2). */
  ueberschrift?: 'h2' | 'h3';
  /** Leiste: Der Text rollt in sich, Auswahl und Suche bleiben stehen; Tabellen werden zu Kästen. */
  eigenerBildlauf?: boolean;
  /** Kachel im schmalen Fenster: Tabellen als Kästen, obwohl die ganze Seite rollt. */
  schmal?: boolean;
  /** Unterkapitel aus der Adresse: Einstieg an dieser Stelle. */
  startAbschnitt?: string | null;
  lader?: HandbuchLader;
}

function ladeHinweis(text: string) {
  return (
    <Box
      data-testid="handbuch-laedt"
      aria-live="polite"
      sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, color: 'text.secondary' }}
    >
      <CircularProgress size={16} />
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
}

export default function HandbuchGeruest({
  auswahl,
  onAuswahl,
  alleHandbuecher = false,
  handbuecher = HANDBUECHER,
  ueberschrift = 'h3',
  eigenerBildlauf = false,
  schmal = false,
  startAbschnitt = null,
  lader,
}: Props) {
  const [suche, setSuche] = useState('');
  const [sprung, setSprung] = useState<Sprung | null>(startAbschnitt ? { abschnittId: startAbschnitt } : null);
  const gefunden = kapitelFinden(auswahl, handbuecher);
  const handbuch = gefunden?.handbuch ?? handbuchFinden(auswahl.handbuchId, handbuecher) ?? handbuecher[0];
  const sucheAktiv = suche.trim().length > 0;
  const suchbereich = useMemo(
    () => (alleHandbuecher ? handbuecher : [handbuch]),
    [alleHandbuecher, handbuecher, handbuch],
  );

  // Der Text des gelesenen Handbuchs immer; alle Handbücher erst, wenn wirklich gesucht wird.
  const benoetigt = useMemo(
    () => (sucheAktiv ? suchbereich.map((eintrag) => eintrag.id) : [handbuch.id]),
    [sucheAktiv, suchbereich, handbuch.id],
  );
  const { texte, status, neuLaden } = useHandbuchTexte(benoetigt, lader);
  const treffer = useMemo(() => handbuchSuchen(suche, suchbereich, texte), [suche, suchbereich, texte]);
  const kapitelText = texte[handbuch.id]?.kapitel.find((eintrag) => eintrag.id === gefunden?.kapitel.id);

  const handbuchWaehlen = (handbuchId: string) => {
    const ziel = handbuchFinden(handbuchId, handbuecher);
    if (ziel && ziel.kapitel.length > 0) {
      setSprung(null);
      setSuche('');
      onAuswahl({ handbuchId: ziel.id, kapitelId: ziel.kapitel[0].id });
    }
  };
  const kapitelWaehlen = (kapitelId: string) => {
    setSprung(null);
    setSuche('');
    onAuswahl({ handbuchId: handbuch.id, kapitelId });
  };
  const verweisFolgen = (ziel: HandbuchZiel) => {
    setSprung({ abschnittId: ziel.abschnitt, oben: !ziel.abschnitt });
    onAuswahl({ handbuchId: ziel.handbuch, kapitelId: ziel.kapitel }, ziel.abschnitt);
  };

  // Bildlauf: Ein Sprung geht vor, sobald der Text da ist; sonst beginnt in der Leiste das neue Kapitel oben.
  const rollerRef = useRef<HTMLDivElement>(null);
  const kopfRef = useRef<HTMLHeadingElement>(null);
  const kapitelSchluessel = `${auswahl.handbuchId}/${auswahl.kapitelId}`;
  const bisherSchluessel = useRef(kapitelSchluessel);
  const erledigterSprung = useRef<Sprung | null>(null);
  const anzeigeBereit = Boolean(kapitelText) && !sucheAktiv;
  useLayoutEffect(() => {
    if (!anzeigeBereit) return;
    const wurzel = rollerRef.current;
    if (!wurzel) return;
    const zuElement = (element: Element | null) => {
      if (!element) return;
      if (eigenerBildlauf) {
        wurzel.scrollTop += element.getBoundingClientRect().top - wurzel.getBoundingClientRect().top;
      } else if (element.scrollIntoView) {
        element.scrollIntoView({ block: 'start' });
      }
    };
    const kapitelGewechselt = bisherSchluessel.current !== kapitelSchluessel;
    bisherSchluessel.current = kapitelSchluessel;
    // Der Sprung gilt einmal je Auslöser; Markierungen bleiben bis zur nächsten Wahl.
    const offenerSprung = sprung !== erledigterSprung.current ? sprung : null;
    erledigterSprung.current = sprung;
    if (offenerSprung?.abschnittId) {
      // Die Kennung kann aus der Adresse stammen, deshalb vergleichen statt in einen Selektor einzusetzen.
      const { abschnittId } = offenerSprung;
      const abschnitte = Array.from(wurzel.querySelectorAll<HTMLElement>('[data-abschnitt]'));
      zuElement(abschnitte.find((element) => element.dataset.abschnitt === abschnittId) ?? null);
    } else if (offenerSprung?.block !== undefined) {
      zuElement(wurzel.querySelector(`[data-handbuch-block="${offenerSprung.block}"]`));
    } else if (offenerSprung?.oben) {
      if (eigenerBildlauf) wurzel.scrollTop = 0;
      else zuElement(kopfRef.current);
    } else if (kapitelGewechselt && eigenerBildlauf) {
      // Auf der Kachel steht der neue Text direkt unter der Auswahl, dort bleibt die Seite stehen.
      wurzel.scrollTop = 0;
    }
  }, [anzeigeBereit, kapitelSchluessel, sprung, eigenerBildlauf]);
  useEffect(() => {
    if (sucheAktiv && rollerRef.current && eigenerBildlauf) rollerRef.current.scrollTop = 0;
  }, [sucheAktiv, eigenerBildlauf]);

  const trefferWaehlen = (eintrag: (typeof treffer)[number]) => {
    setSprung({ block: eintrag.block, woerter: suchwoerter(suche), oben: eintrag.block === undefined });
    onAuswahl({ handbuchId: eintrag.handbuch.id, kapitelId: eintrag.kapitel.id });
    setSuche('');
  };

  const woerter = sprung?.woerter ?? [];
  const schmaleAnzeige = eigenerBildlauf || schmal;

  return (
    <Box
      data-testid="handbuch-geruest"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(eigenerBildlauf ? { flex: '1 1 auto', minHeight: 0 } : {}),
      }}
    >
      <Box
        data-testid="handbuch-bedienung"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          ...(eigenerBildlauf ? { flex: '0 0 auto', p: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' } : {}),
        }}
      >
        {alleHandbuecher && (
          <TextField
            select
            size="small"
            label={HANDBUCH_AUSWAHL_FELDNAME}
            value={handbuch.id}
            onChange={(event) => handbuchWaehlen(event.target.value)}
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': HANDBUCH_AUSWAHL_FELDNAME, 'data-testid': 'handbuch-auswahl' }}
          >
            {handbuecher.map((eintrag) => (
              <option key={eintrag.id} value={eintrag.id}>
                {eintrag.titel}
              </option>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label={KAPITEL_AUSWAHL_FELDNAME}
          value={gefunden ? gefunden.kapitel.id : ''}
          onChange={(event) => kapitelWaehlen(event.target.value)}
          SelectProps={{ native: true }}
          inputProps={{ 'aria-label': KAPITEL_AUSWAHL_FELDNAME, 'data-testid': 'handbuch-kapitel-auswahl' }}
        >
          {handbuch.kapitel.map((kapitel) => (
            <option key={kapitel.id} value={kapitel.id}>
              {kapitelBezeichnung(kapitel)}
            </option>
          ))}
        </TextField>
        <TextField
          size="small"
          type="search"
          placeholder={alleHandbuecher ? HANDBUCH_SUCHE_ALLE : HANDBUCH_SUCHE_LEISTE}
          value={suche}
          onChange={(event) => setSuche(event.target.value)}
          inputProps={{
            'aria-label': alleHandbuecher ? HANDBUCH_SUCHE_ALLE : HANDBUCH_SUCHE_LEISTE,
            'data-testid': 'handbuch-suche',
          }}
        />
      </Box>
      <Box
        ref={rollerRef}
        data-testid="handbuch-rollbereich"
        sx={{
          minWidth: 0,
          ...(eigenerBildlauf
            ? { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', p: 1.5 }
            : { mt: 1.5 }),
        }}
      >
        {sucheAktiv ? (
          <Box data-testid="handbuch-treffer" aria-live="polite">
            {status === 'laedt' && ladeHinweis('Die Suche im Text wird vorbereitet …')}
            {status === 'fehler' && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={neuLaden}>
                    {HANDBUCH_ERNEUT}
                  </Button>
                }
              >
                {HANDBUCH_SUCHE_FEHLER}
              </Alert>
            )}
            {treffer.length === 0 && status !== 'laedt' ? (
              <Typography variant="body2" color="text.secondary">
                {HANDBUCH_KEINE_TREFFER}
              </Typography>
            ) : null}
            {treffer.length > 0 ? (
              <List dense disablePadding aria-label="Treffer">
                {treffer.map((eintrag) => (
                  <ListItemButton
                    key={`${eintrag.handbuch.id}/${eintrag.kapitel.id}`}
                    onClick={() => trefferWaehlen(eintrag)}
                    data-testid="handbuch-treffer-eintrag"
                    sx={{ borderRadius: 1, display: 'block' }}
                  >
                    <ListItemText
                      primary={kapitelBezeichnung(eintrag.kapitel)}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <>
                          {alleHandbuecher ? <div>{eintrag.handbuch.titel}</div> : null}
                          {eintrag.ausschnitt ? (
                            <Box
                              component="div"
                              sx={{ '& mark': { borderRadius: 0.25, bgcolor: 'warning.light', color: 'common.black' } }}
                            >
                              {textMarkieren(eintrag.ausschnitt, suchwoerter(suche)).map((stueck, stelle) =>
                                stueck.markiert ? (
                                  // eslint-disable-next-line react/no-array-index-key -- Ausschnitt ist unveränderlich
                                  <mark key={stelle}>{stueck.text}</mark>
                                ) : (
                                  // eslint-disable-next-line react/no-array-index-key -- s. o.
                                  <React.Fragment key={stelle}>{stueck.text}</React.Fragment>
                                ),
                              )}
                            </Box>
                          ) : null}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : null}
          </Box>
        ) : (
          <>
            {gefunden ? (
              <Typography
                ref={kopfRef}
                component={ueberschrift}
                variant="subtitle1"
                data-testid="handbuch-kapitel-ueberschrift"
                sx={{ fontWeight: 600, mb: 1, scrollMarginTop: 96 }}
              >
                {kapitelBezeichnung(gefunden.kapitel)}
              </Typography>
            ) : null}
            {status === 'laedt' && !kapitelText && ladeHinweis(HANDBUCH_TEXT_LAEDT)}
            {status === 'fehler' && !kapitelText && (
              <Alert
                severity="error"
                data-testid="handbuch-fehler"
                action={
                  <Button color="inherit" size="small" onClick={neuLaden}>
                    {HANDBUCH_ERNEUT}
                  </Button>
                }
              >
                {HANDBUCH_TEXT_FEHLER}
              </Alert>
            )}
            {kapitelText ? (
              <HandbuchText
                bloecke={kapitelText.bloecke}
                kontext={{
                  handbuchId: handbuch.id,
                  nurEinHandbuch: !alleHandbuecher,
                  schmal: schmaleAnzeige,
                  unterkapitelEbene: ueberschrift === 'h2' ? 3 : 4,
                  woerter,
                  onVerweis: verweisFolgen,
                }}
              />
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}
